import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Alert,
  Typography,
  Space,
  Descriptions,
  Tag,
  message,
  Popconfirm,
  Divider
} from 'antd';
import {
  LinkOutlined,
  LockOutlined,
  UnlockOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import portalService from '../services/patientPortalService';
import { useAuth } from '../context/AuthContext';
import PatientPortalPasswordReset from './PatientPortalPasswordReset';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const PatientPortalAccessManager = ({ patient, onPasswordReset }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [portalStatus, setPortalStatus] = useState(null);
  const [extendLoading, setExtendLoading] = useState(false);

  // Immediately check for required role
  const hasRequiredRole = user && ['Admin', 'MD', 'Nursing'].includes(user.role);

  useEffect(() => {
    if (patient?._id && hasRequiredRole) {
      fetchPortalStatus();
    } else if (!hasRequiredRole) {
      setLoading(false);
    }
  }, [patient?._id, hasRequiredRole]);

  if (!hasRequiredRole) {
    return (
      <Card
        title={
          <Space>
            <LockOutlined />
            Patient Portal Management
          </Space>
        }
      >
        <Alert
          message="Insufficient Permissions"
          description={`You need Admin, MD, or Nursing role to manage patient portal access. Your current role: ${user?.role || 'Unknown'}`}
          type="warning"
          showIcon
        />
      </Card>
    );
  }

  const fetchPortalStatus = async () => {
    try {
      setLoading(true);
      const response = await portalService.getPortalStatus(patient._id);
      setPortalStatus(response.data);
    } catch (error) {
      console.error('Error fetching portal status:', error);
      message.error('Failed to load portal status');
    } finally {
      setLoading(false);
    }
  };



  const getStatusTag = () => {
    if (!portalStatus?.hasPortalAccess) {
      return <Tag color="default">No Access</Tag>;
    }

    const { portalAccess } = portalStatus;
    
    if (!portalAccess.isEnabled) {
      return <Tag color="red" icon={<LockOutlined />}>Disabled</Tag>;
    }
    
    if (portalAccess.isExpired) {
      return <Tag color="orange" icon={<ClockCircleOutlined />}>Expired</Tag>;
    }
    
    if (portalAccess.isLocked) {
      return <Tag color="red" icon={<LockOutlined />}>Locked</Tag>;
    }
    
    return <Tag color="green" icon={<CheckCircleOutlined />}>Active</Tag>;
  };

  const renderPortalStatus = () => {
    if (!portalStatus?.hasPortalAccess) {
      return (
        <Alert
          message="No Portal Access"
          description="This patient does not have portal access yet. Use the 'Reset/Create Password' button below to create portal access and generate login credentials for the patient."
          type="info"
          showIcon
        />
      );
    }

    const { portalAccess } = portalStatus;

    const handleNavigateToPortal = () => {
      // Navigate to staff preview of patient portal
      navigate(`/portal/staff-preview/${patient._id}`);
    };

    const handleExtendAccess = async (days = 90) => {
      try {
        setExtendLoading(true);
        await portalService.extendPortalAccess(patient._id, days);
        message.success(`Portal access extended by ${days} days successfully`);

        // Refresh portal status
        fetchPortalStatus();
      } catch (error) {
        console.error('Error extending portal access:', error);
        message.error('Failed to extend portal access');
      } finally {
        setExtendLoading(false);
      }
    };

    return (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Status">
            {getStatusTag()}
          </Descriptions.Item>
          <Descriptions.Item label="Created">
            {new Date(portalAccess.createdAt).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Created By">
            {portalAccess.createdBy?.name || 'Unknown'}
          </Descriptions.Item>
          <Descriptions.Item label="Expires">
            {new Date(portalAccess.expiresAt).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Last Login">
            {portalAccess.lastLoginAt
              ? new Date(portalAccess.lastLoginAt).toLocaleString()
              : 'Never'
            }
          </Descriptions.Item>
        </Descriptions>

        {/* Action Buttons */}
        <Space direction="horizontal" style={{ width: '100%' }} size="middle">
          <Button
            type="primary"
            icon={<GlobalOutlined />}
            onClick={handleNavigateToPortal}
            style={{
              borderRadius: '8px',
              height: '40px',
              fontWeight: '500',
              background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
              border: 'none',
              boxShadow: '0 4px 12px rgba(24, 144, 255, 0.2)',
              flex: 1
            }}
            size="large"
          >
            Preview Patient Portal
          </Button>

          {/* Extend Access Button - shown for expired or about to expire access */}
          {(portalAccess.isExpired ||
            (portalAccess.expiresAt && new Date(portalAccess.expiresAt) - new Date() < 7 * 24 * 60 * 60 * 1000)) && (
            <Popconfirm
              title="Extend Portal Access"
              description="Are you sure you want to extend this patient's portal access by 90 days?"
              onConfirm={() => handleExtendAccess(90)}
              okText="Yes, Extend"
              cancelText="Cancel"
            >
              <Button
                type={portalAccess.isExpired ? "primary" : "default"}
                icon={<ClockCircleOutlined />}
                loading={extendLoading}
                style={{
                  borderRadius: '8px',
                  height: '40px',
                  fontWeight: '500',
                  background: portalAccess.isExpired ? 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)' : undefined,
                  border: portalAccess.isExpired ? 'none' : undefined,
                  boxShadow: portalAccess.isExpired ? '0 4px 12px rgba(82, 196, 26, 0.2)' : undefined,
                  color: portalAccess.isExpired ? 'white' : undefined
                }}
                size="large"
              >
                {portalAccess.isExpired ? 'Reactivate' : 'Extend'}
              </Button>
            </Popconfirm>
          )}
        </Space>
      </Space>
    );
  };



  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {/* Portal Status Card */}
      <Card
        title={
          <Space>
            <LockOutlined />
            Patient Portal Status
          </Space>
        }
        loading={loading}
        size="small"
      >
        {renderPortalStatus()}
      </Card>

      {/* Password Reset Component */}
      <PatientPortalPasswordReset
        patient={patient}
        onPasswordReset={(resetData) => {
          // Refresh portal status after password reset
          fetchPortalStatus();
          if (onPasswordReset) {
            onPasswordReset(resetData);
          }
        }}
      />
    </Space>
  );
};

export default PatientPortalAccessManager;
